/**
 * 특정 딥링크를 탭으로 매핑. `useOpenDeeplink` 에서 매치되면 bridge 대신 RN navigation 으로
 * 직접 이동합니다.
 *
 * 두 종류를 구분합니다:
 * - **홈 상단탭** (`HOME_TAB_BY_DEEPLINK_PATH`): 하단탭 HOME 안의 탭. `selected` 파라미터로 지정.
 * - **하단탭** (`BOTTOM_TAB_BY_DEEPLINK_PATH`): 하단탭 자체. 탭 route name 으로 이동.
 *
 * 확장 시 해당 매핑에 path 를 추가하세요. key 는 `ably://` 뒤 path 이며 **완전 일치**입니다
 * (`mypage` 는 매치되지만 `mypage/alarm-setting` 은 매치되지 않아 전용 화면으로 흘러갑니다).
 */

import { extractWebviewUrlParam } from './resolveWebviewDeeplinkUrl';

type HomeTabMapping = {
  /** HomeContents의 `item.route.screen_name`과 매칭되는 값 */
  screenName: string;
  /** 딥링크 쿼리에서 함께 실어 보낼 파라미터 키 */
  passQueryKeys?: readonly string[];
};

/** key: `ably://` 뒤의 path 부분 (예: `ranking/market`) */
const HOME_TAB_BY_DEEPLINK_PATH: Record<string, HomeTabMapping> = {
  // 소비처(useOpenDeeplink)가 screenName 을 소문자로 바꿔 `selected` 로 넘기고,
  // HomeContents 는 그 값을 `item.id` 또는 `item.screen_name` 과 매칭한다.
  // 홈 첫 탭의 id 가 `today` 이므로 여기 값은 대문자 `TODAY` 여야 한다.
  // (빈 문자열이면 매칭 실패 → selected_id 폴백이라 이동이 무의미해진다)
  'home/today': {
    screenName: 'TODAY',
    passQueryKeys: [],
  },
  ranking: {
    screenName: 'MARKET_RANKING',
    passQueryKeys: ['market_type_sno'],
  },
  'ranking/market': {
    screenName: 'MARKET_RANKING',
    passQueryKeys: ['market_type_sno'],
  },
  'ranking/goods': {
    screenName: 'RANKING',
    passQueryKeys: [''],
  },
};

/**
 * key: 목적지 path(선행 슬래시 포함) → 하단탭 route name.
 *
 * route name 은 mainTabs(API) 의 `screen_name` 이다(BottomTabNavigator 가 그 값으로 등록).
 * linking path 는 `NavigationWithModalProvider` 의 `WEBVIEW_TAB_PATHS` / `HomeTabScreens` 참고.
 */
const BOTTOM_TAB_BY_PATH: Record<string, string> = {
  '/mypage': 'MY_PAGE',
};

export type HomeTabDeeplinkResult = {
  screenName: string;
  params: Record<string, string>;
};

const getQueryValue = (query: string, key: string): string | null => {
  const match = query.match(new RegExp(`(?:^|&)${key}=([^&]*)`));
  return match ? match[1] : null;
};

/** `ably://<path>?<query>` 를 path/query 로 분해. 딥링크가 아니면 null. */
const parseDeeplink = (
  link: string,
): { path: string; query: string } | null => {
  const match = link.match(/^ably:\/\/([^?]+?)\/?(?:\?(.*))?$/);
  if (!match) return null;
  return { path: match[1], query: match[2] ?? '' };
};

/** 절대 URL 이나 path 를 `/foo` 형태 pathname 으로 정규화. */
const toPathname = (urlOrPath: string): string => {
  try {
    return new URL(urlOrPath).pathname;
  } catch {
    const [rawPath] = urlOrPath.split('?');
    const withSlash = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    // 후행 슬래시 제거(루트는 유지)
    return withSlash.length > 1 && withSlash.endsWith('/')
      ? withSlash.slice(0, -1)
      : withSlash;
  }
};

/**
 * 하단탭으로 바로 이동해야 하는 딥링크면 그 탭의 route name. 아니면 null.
 *
 * 두 형태를 모두 받는다:
 * - `ably://mypage`
 * - `ably://webview?url=http://localhost:3000/mypage` (감싸진 형태 — 실제로 이렇게 들어온다)
 */
export const resolveBottomTabFromDeeplink = (link: string): string | null => {
  const parsed = parseDeeplink(link);
  if (parsed === null) return null;

  // 감싸진 형태는 path 가 'webview' 이고 목적지가 url 파라미터 안에 있다.
  if (parsed.path === 'webview') {
    const urlParam = extractWebviewUrlParam(link);
    if (urlParam === null) return null;
    return BOTTOM_TAB_BY_PATH[toPathname(urlParam)] ?? null;
  }

  return BOTTOM_TAB_BY_PATH[toPathname(parsed.path)] ?? null;
};

export const resolveHomeTabFromDeeplink = (
  link: string,
): HomeTabDeeplinkResult | null => {
  const parsed = parseDeeplink(link);
  if (parsed === null) return null;
  const { path, query } = parsed;

  const mapping = HOME_TAB_BY_DEEPLINK_PATH[path];
  if (!mapping) return null;

  const params: Record<string, string> = {};
  if (mapping.passQueryKeys && query) {
    for (const key of mapping.passQueryKeys) {
      const value = getQueryValue(query, key);
      if (value != null) params[key] = value;
    }
  }
  return { screenName: mapping.screenName, params };
};
